import { HugeiconsIcon } from "@hugeicons/react";
import {
	AdvertisimentIcon as AdvertisimentIconDuotone,
	NoteEditIcon as NoteEditIconDuotone,
	Settings05Icon as Settings05IconDuotone,
} from "@hugeicons-pro/core-duotone-rounded";
import {
	AdvertisimentIcon,
	NoteEditIcon,
	PodcastIcon,
	Settings05Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Trans, useLingui } from "@lingui/react/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { AppContainer } from "~/components/layouts/app/app-container";
import { NavigationTabLink } from "~/components/navigation-tab-link";
import { Badge } from "~/components/ui/badge";
import { PageHead } from "~/components/ui/page-head";
import { ScrollArea, ScrollBar } from "~/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import type { Channel } from "~/models/channel";
import type { Episode } from "~/models/episode";
import { episodeStatusLabel } from "~/models/episode.helpers";
import { seo } from "~/modules/page/seo";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute(
	"/_app/podcasts/$channelUid/episodes/$episodeUid",
)({
	component: Page,
	async loader({ context, params }) {
		const [episode] = await Promise.all([
			context.queryClient.ensureQueryData(
				context.trpc.episode.find.queryOptions({
					episodeUid: params.episodeUid,
				}),
			),
		]);

		if (episode.channelsUID !== params.channelUid) {
			throw new Error("Episode does not belong to the specified channel.");
		}

		return {
			meta: {
				title: episode.title,
			},
		};
	},
	head: ({ loaderData }) => ({
		meta: [
			...seo({
				title: `${loaderData?.meta.title} | Azerion`,
				description: "Host & Monetize your podcasts.",
			}),
		],
	}),
});

function Page() {
	const { channelUid, episodeUid } = Route.useParams();

	const { t } = useLingui();
	const trpc = useTRPC();

	const { data: episode } = useSuspenseQuery(
		trpc.episode.find.queryOptions({ episodeUid }),
	);

	return (
		<>
			<PageHead
				back={{
					to: "/podcasts/$channelUid/episodes",
					params: { channelUid },
					label: t`All Episodes`,
				}}
				avatar={{
					src: episode.iconsThumbnailURL,
					alt: episode.title,
					fallback: <HugeiconsIcon icon={PodcastIcon} />,
				}}
				title={episode.title}
				trackUrl={episode.assetsFilePath}
				subtitle={
					<>
						<span>
							<Trans>Edit this episode details.</Trans>
						</span>

						<Badge variant="status" status={episode.status} className="ml-2">
							<span>{episodeStatusLabel(episode.status)}</span>
						</Badge>
					</>
				}
				navigation={
					<EpisodeNavigation channelUid={channelUid} episodeUid={episodeUid} />
				}
			/>
			<AppContainer className="my-12">
				<div className="flex items-start gap-6">
					<Outlet />
				</div>
			</AppContainer>
		</>
	);
}

function EpisodeNavigation({
	channelUid,
	episodeUid,
}: {
	channelUid: Channel["uid"];
	episodeUid: Episode["uid"];
}) {
	return (
		<Tabs>
			<ScrollArea className="-mb-[2px]">
				<TabsList className="text-foreground h-auto gap-2 rounded-none bg-transparent px-0">
					<TabsTrigger value="statistics" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/episodes/$episodeUid/details"
							params={{ channelUid, episodeUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={NoteEditIcon}
										altIcon={NoteEditIconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 opacity-60 size-4"
									/>
									<Trans>Details</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
					<TabsTrigger value="episodes" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/episodes/$episodeUid/monetization"
							params={{ channelUid, episodeUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={AdvertisimentIcon}
										altIcon={AdvertisimentIconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 opacity-60 size-4"
									/>
									<Trans>Monetization</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
					<TabsTrigger value="settings" asChild>
						<NavigationTabLink
							to="/podcasts/$channelUid/episodes/$episodeUid/settings"
							params={{ channelUid, episodeUid }}
						>
							{({ isActive }) => (
								<>
									<HugeiconsIcon
										icon={Settings05Icon}
										altIcon={Settings05IconDuotone}
										showAlt={isActive}
										className="-ms-0.5 me-1.5 opacity-60 size-4"
									/>
									<Trans>Settings</Trans>
								</>
							)}
						</NavigationTabLink>
					</TabsTrigger>
				</TabsList>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>
		</Tabs>
	);
}
